import type { SharePayload, ShareSite } from './types.ts'
import { truncateRunes } from './text.ts'

export type ShareChannelId = 'wechat' | 'qq' | 'weibo' | 'x' | 'facebook'

export interface ChannelLink {
  id: ShareChannelId
  label: string
  /** 微信没有跳转协议，由弹窗展示二维码或操作引导 */
  href: string | null
}

const WEIBO_RUNES = 140

/** 只接受 http(s) 绝对地址，避免把 javascript: 等协议送进分享窗口。 */
export function safeShareUrl(url: string): string | null {
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null
    return parsed.toString()
  } catch {
    return null
  }
}

/** 复制到剪贴板的完整文案。includeSecret 为假时不出现保密内容。 */
export function buildShareText(payload: SharePayload, site: ShareSite, includeSecret: boolean): string {
  const parts: string[] = []
  const headline = payload.headline.trim()
  const text = payload.text.trim()
  if (headline) parts.push(headline)
  if (text && text !== headline) parts.push(text)
  if (includeSecret) {
    const secret = payload.secret?.trim()
    if (secret) parts.push(secret)
  }
  const url = safeShareUrl(payload.url)
  if (url) parts.push(url)
  const tags = site.hashtags.map((tag) => tag.replace(/^#/, '').trim()).filter(Boolean)
  if (tags.length > 0) parts.push(tags.map((tag) => `#${tag}`).join(' '))
  return parts.join('\n')
}

function pitch(payload: SharePayload, includeSecret: boolean): string {
  const parts: string[] = []
  const headline = payload.headline.trim()
  const text = payload.text.trim()
  if (headline) parts.push(headline)
  if (text && text !== headline) parts.push(text)
  if (includeSecret) {
    const secret = payload.secret?.trim()
    if (secret) parts.push(secret)
  }
  return parts.join('\n')
}

/**
 * 各平台跳转地址。
 * Facebook 只认落地页的 Open Graph，不传 quote。
 * 任何渠道的 url 参数都是落地页本身，不把 secret 编进去。
 */
export function channelLinks(payload: SharePayload, site: ShareSite, includeSecret: boolean): ChannelLink[] {
  const url = safeShareUrl(payload.url)
  const summary = pitch(payload, includeSecret)
  const encodedUrl = url ? encodeURIComponent(url) : ''
  const tags = site.hashtags.map((tag) => tag.replace(/^#/, '').trim()).filter(Boolean)
  const qqHref = url
    ? `https://connect.qq.com/widget/shareqq/index.html?url=${encodedUrl}&title=${encodeURIComponent(payload.headline)}&summary=${encodeURIComponent(summary)}&site=${encodeURIComponent(site.name)}`
    : null
  const weiboHref = url
    ? `https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodeURIComponent(truncateRunes(summary, WEIBO_RUNES))}`
    : null
  const xHref = url
    ? `https://x.com/intent/tweet?text=${encodeURIComponent(summary)}&url=${encodedUrl}&hashtags=${encodeURIComponent(tags.join(','))}`
    : null
  const facebookHref = url ? `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` : null

  return [
    { id: 'wechat', label: '微信', href: null },
    { id: 'qq', label: 'QQ', href: qqHref },
    { id: 'weibo', label: '微博', href: weiboHref },
    { id: 'x', label: 'X', href: xHref },
    { id: 'facebook', label: 'Facebook', href: facebookHref },
  ]
}
